import React from 'react';
import moment, { Moment } from 'moment';
import classNames from 'classnames';
import { inject } from 'mobx-react';
import chartStore, { maxRanges } from './ChartStore';
import GQPopupStorage from '../GQPopup/GQPopupStorage';
import { C_TIMELINE_MAX_DATE, C_TIMELINE_MIN_DATE, MOMENTJS_DATE_DISPLAY_FORMAT } from '../../constants';
import { isHeadlessMode } from '../../services/configService';
import { FGIcons } from '@fg/design-system';
import { IMobxRootState } from '../../RootStore';
export interface ISliderHorizontalProps {
    baseDate: moment.Moment;
    displayedMinX: moment.Moment;
    displayedMaxX: moment.Moment;
    minimumPossibleX: moment.Moment;
    maximumPossibleX: moment.Moment;
    onPan: (date: moment.Moment) => void;
    expanded?: boolean;
    rangeLeft: number;
    rangeRight: number;
    virtualToday: moment.Moment;
    rangeLeftDate: moment.Moment;
    rangeRightDate: moment.Moment;
}

enum E_EdgeDrag {
    left = 1,
    right = 2,
}
interface ISliderHorizontalState {
    dragging: boolean;
    edgeDrag: E_EdgeDrag;
    rel: { x: number; date: moment.Moment };
}

export default class SliderHorizontal extends React.Component<
    ISliderHorizontalProps,
    ISliderHorizontalState
> {
    private parent: HTMLDivElement = null;
    constructor(props: ISliderHorizontalProps) {
        super(props);
        this.state = {
            dragging: false,
            edgeDrag: null,
            rel: null,
        };
    }

    public componentDidUpdate(
        props: ISliderHorizontalProps,
        state: ISliderHorizontalState
    ) {
        if (this.state.dragging && !state.dragging) {
            document.addEventListener('mousemove', this.onMouseMove as any);
            document.addEventListener('mouseup', this.onMouseUp as any);
        } else if (!this.state.dragging && state.dragging) {
            document.removeEventListener('mousemove', this.onMouseMove as any);
            document.removeEventListener('mouseup', this.onMouseUp as any);
        }
    }
    public render() {
        if (isHeadlessMode()) {
            return null;
        }
        const dragClass = classNames(['range-slider-draggable'], {
            dragged: this.state.dragging,
        });
        const maxRange = this.props.maximumPossibleX.diff(
            this.props.minimumPossibleX,
            'days'
        );
        const currentZoomedRange =
            this.props.displayedMaxX.diff(this.props.displayedMinX, 'days') +
            80;
        const width = (currentZoomedRange / maxRange) * 100;
        const left =
            Math.abs(
                this.props.displayedMinX.diff(
                    this.props.minimumPossibleX,
                    'days'
                ) / maxRange
            ) * 100;
        const pickerStyle: React.CSSProperties = {
            width: `${width}%`,
            left: `${left}%`,
        };
        return (
            <div
                className="range-horizontal-slider"
                ref={e => (this.parent = e)}>
                <div className="chart-year-start">
                    {C_TIMELINE_MIN_DATE.year()}
                </div>
                <div className="chart-year-end">
                    {C_TIMELINE_MAX_DATE.year()}
                </div>
                <div
                    className={dragClass}
                    style={{ ...pickerStyle }}
                    onMouseDown={this.onMouseDown}>
                    <div
                        id="leftHandle"
                        className="range-handle"
                        onMouseDown={this.onMouseDown}
                        onMouseUp={this.onMouseUp}>
                        <FGIcons icon="LeftArrow" color="white" />
                    </div>
                    <div
                        id="rightHandle"
                        className="range-handle"
                        onMouseDown={this.onMouseDown}
                        onMouseUp={this.onMouseUp}>
                        <FGIcons icon="RightArrow" color="white" />
                    </div>
                </div>
            </div>
        );
    }

    private tooltipData(date: moment.Moment) {
        return (
            <span className="range-handle-tooltip-content">
                {date.format(MOMENTJS_DATE_DISPLAY_FORMAT)}
            </span>
        );
    }
    private addPopup(isLeft: boolean, el: HTMLDivElement) {
        GQPopupStorage.addData(
            isLeft ? 'left-handle-tooltip' : 'right-handle-tooltip',
            {
                element: el,
                orientation: isLeft ? 'top left' : 'top right',
                tooltipData: this.tooltipData(
                    isLeft ? this.props.displayedMinX : this.props.displayedMaxX
                ),
            }
        );
    }

    private removePopup(isLeft: boolean) {
        GQPopupStorage.addData(
            isLeft ? 'left-handle-tooltip' : 'right-handle-tooltip',
            null
        );
    }

    private onMouseUp = (e: MouseEvent | React.MouseEvent<HTMLDivElement>) => {
        this.removePopup(true);
        this.removePopup(false);
        this.setState({ dragging: false });
        chartStore.setState({
            isDragging: false,
        });
        e.stopPropagation();
        e.preventDefault();
    };

    private onMouseMove = (e: MouseEvent) => {
        if (!this.state.dragging) {
            return;
        }
        const daysMoved =
            ((e.pageX - this.state.rel.x) / this.parent.clientWidth) *
            this.props.maximumPossibleX.diff(
                this.props.minimumPossibleX,
                'days'
            );
        if (this.state.edgeDrag) {
            if (this.state.edgeDrag === E_EdgeDrag.left) {
                const newLeftDate = this.state.rel.date
                    .clone()
                    .add(Math.round(daysMoved), 'days');
                chartStore.setNewRanges(
                    this.props.baseDate.diff(newLeftDate, 'days'),
                    this.props.rangeRight
                );
                GQPopupStorage.updateData('left-handle-tooltip', {
                    tooltipData: this.tooltipData(this.props.rangeLeftDate),
                });
                this.applyVirtualTodayBounds();
            } else {
                const newRightDate = this.state.rel.date
                    .clone()
                    .add(Math.round(daysMoved), 'days');
                chartStore.setNewRanges(
                    this.props.rangeLeft,
                    newRightDate.diff(this.props.baseDate, 'days')
                );
                GQPopupStorage.updateData('right-handle-tooltip', {
                    tooltipData: this.tooltipData(this.props.rangeRightDate),
                });
                this.applyVirtualTodayBounds();
            }
        } else {
            this.props.onPan(
                this.state.rel.date.clone().add(Math.round(daysMoved), 'days')
            );
        }
        e.stopPropagation();
        e.preventDefault();
    };

    private applyVirtualTodayBounds() {
        const maxVirtualTodayPossibleLeft = this.props.baseDate
            .clone()
            .startOf('day')
            .subtract(chartStore.rangeLeft, 'days');
        const maxVirtualTodayPossibleRight = this.props.baseDate
            .clone()
            .startOf('day')
            .add(this.props.rangeRight, 'days');
        if (
            this.props.virtualToday.isBefore(maxVirtualTodayPossibleLeft, 'd')
        ) {
            this.onVirualTodayChange(maxVirtualTodayPossibleLeft);
        }

        if (
            this.props.virtualToday.isAfter(maxVirtualTodayPossibleRight, 'd')
        ) {
            this.onVirualTodayChange(maxVirtualTodayPossibleRight);
        }
    }

    private onVirualTodayChange = (date: moment.Moment) => {
        if (!date.isSame(this.props.virtualToday, 'd')) {
            chartStore.setState({
                virtualToday: date.clone(),
            });
        }
    };

    private onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.button !== 0) {
            return;
        }
        let date: moment.Moment = null;
        let edgeDrag: E_EdgeDrag = null;
        switch (true) {
            case e.currentTarget.id === 'leftHandle':
                date = this.props.displayedMinX;
                edgeDrag = E_EdgeDrag.left;
                this.addPopup(true, e.currentTarget);
                break;
            case e.currentTarget.id === 'rightHandle':
                date = this.props.displayedMaxX;
                edgeDrag = E_EdgeDrag.right;
                this.addPopup(false, e.currentTarget);
                break;
            default:
                date = this.props.baseDate;
        }
        this.setState({
            dragging: true,
            edgeDrag,
            rel: {
                x: e.pageX,
                date: date.clone(),
            },
        });
        chartStore.setState({
            isDragging: true,
        });
        e.stopPropagation();
        e.preventDefault();
    };
}


type SliderHorizontalProviderProps = {
    baseDate?: moment.Moment;
    rangeLeft?: number;
    rangeRight?: number;
    virtualToday?: moment.Moment;
    rangeLeftDate?: moment.Moment;
    rangeRightDate?: moment.Moment;
};

export const SliderHorizontalProvider = inject(
    ({ chartStore }: IMobxRootState): SliderHorizontalProviderProps => ({
        baseDate: chartStore.baseDate,
        rangeLeft: chartStore.rangeLeft,
        rangeRight: chartStore.rangeRight,
        virtualToday: chartStore.virtualToday,
        rangeLeftDate: chartStore.rangeLeftDate,
        rangeRightDate: chartStore.rangeRightDate,
    })
)(
    ({
        baseDate,
        rangeLeft,
        rangeRight,
        virtualToday,
        rangeLeftDate,
        rangeRightDate,
    }: SliderHorizontalProviderProps) => {
        //todo: refactor this function
        const onPanChangeBaseDate = (newDate: Moment) => {
            const maybeNewVirtualToday = chartStore.setNewBaseDate(
                newDate.clone(),
                chartStore.virtualToday
            );
            if (maybeNewVirtualToday) {
                if (
                    !maybeNewVirtualToday.isSame(chartStore.virtualToday, 'd')
                ) {
                    chartStore.setState({
                        virtualToday: maybeNewVirtualToday.clone(),
                    });
                }
            }
        };
        return (
            <SliderHorizontal
                maximumPossibleX={maxRanges.maxBaseDate.clone()}
                minimumPossibleX={maxRanges.minBaseDate.clone()}
                baseDate={baseDate.clone()}
                displayedMinX={baseDate
                    .clone()
                    .startOf('day')
                    .subtract(rangeLeft, 'days')}
                displayedMaxX={baseDate
                    .clone()
                    .endOf('day')
                    .add(rangeRight, 'days')}
                onPan={onPanChangeBaseDate}
                expanded
                virtualToday={virtualToday}
                rangeLeftDate={rangeLeftDate}
                rangeRightDate={rangeRightDate}
                rangeLeft={rangeLeft}
                rangeRight={rangeRight}
            />
        );
    }
);